import { type JSX } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@contexts/AuthContext";
import { useConfig } from "@contexts/ConfigContext";
import { useTour } from "@contexts/TourContext";
import {
  THEME_MODES,
  type ThemeMode,
  useThemeMode,
} from "@contexts/ThemeContext";
import { SUPPORTED_LANGUAGES } from "@/i18n";
import { useChangeLanguage } from "@/i18n/useChangeLanguage";
import {
  Avatar,
  Button,
  Menu,
  MenuDivider,
  MenuItem,
  MenuItemRadio,
  MenuList,
  MenuPopover,
  MenuTrigger,
} from "@fluentui/react-components";

export interface AppUserMenuProps {
  /** Hides the language picker, which an embedding host already provides. */
  isEmbedded?: boolean;
}

export function AppUserMenu({
  isEmbedded = false,
}: AppUserMenuProps = {}): JSX.Element | null {
  const { t, i18n } = useTranslation();
  const { user, logout, clearCache, clearCacheAndCookies, availableRegions } =
    useAuth();
  const { config } = useConfig();
  const { restartTour } = useTour();
  const changeLanguage = useChangeLanguage();
  const { themeMode, setThemeMode } = useThemeMode();
  const navigate = useNavigate();
  const { boardId } = useParams<{ boardId?: string }>();

  if (!user) return null;

  const activeLanguage =
    SUPPORTED_LANGUAGES.find((lang) => i18n.language.startsWith(lang)) ?? "en";

  const handleClearCache = () => {
    clearCache().then(() => window.location.reload());
  };

  const handleClearCacheAndCookies = () => {
    clearCacheAndCookies().then(() => window.location.reload());
  };

  const handleReplayTour = () => {
    if (boardId) {
      navigate(`/${boardId}/tasks`);
    }
    restartTour();
  };

  return (
    <Menu
      checkedValues={{ theme: [themeMode], language: [activeLanguage] }}
      onCheckedValueChange={(_event, { name, checkedItems }) => {
        const [selected] = checkedItems;
        if (!selected) {
          return;
        }

        if (name === "theme") {
          setThemeMode(selected as ThemeMode);
        } else if (name === "language") {
          void changeLanguage(selected);
        }
      }}
    >
      <MenuTrigger disableButtonEnhancement>
        <Button
          appearance="transparent"
          className="app-header__user-btn"
          aria-label={t("layout.userMenu.title")}
          title={t("layout.userMenu.title")}
          id="userMenuDropdown"
        >
          <Avatar name={user.name} size={32} color="colorful" />
        </Button>
      </MenuTrigger>
      <MenuPopover>
        <MenuList className="app-header__user-menu-list">
          {/* User identity */}
          <div className="app-header__user-info">
            <strong>{user.name}</strong>
            <div className="app-header__user-email">{user.email}</div>
            {config?.regionName && (
              <div className="app-header__user-region">
                <strong>{t("layout.userMenu.region")}</strong>{" "}
                {config.regionName}
              </div>
            )}
          </div>

          <MenuDivider />
          {availableRegions.length > 1 && (
            <MenuItem onClick={() => navigate("/regions?switch=1")}>
              {t("layout.userMenu.switchRegion")}
            </MenuItem>
          )}
          {availableRegions.length > 1 && <MenuDivider />}
          <MenuItem onClick={() => navigate("/account")}>
            {t("layout.userMenu.accountSettings")}
          </MenuItem>
          <MenuItem onClick={logout} id="LogoutLink">
            {t("common.actions.logout")}
          </MenuItem>

          <MenuDivider />
          <div className="app-header__menu-section">
            {t("layout.userMenu.preferences")}
          </div>
          <div className="app-header__menu-subsection">
            {t("layout.themeSelector.label")}
          </div>
          {THEME_MODES.map((mode) => (
            <MenuItemRadio key={mode} name="theme" value={mode}>
              {t(`layout.themeSelector.${mode}`)}
            </MenuItemRadio>
          ))}
          {!isEmbedded && (
            <>
              <div className="app-header__menu-subsection">
                {t("layout.languageSelector.label")}
              </div>
              {SUPPORTED_LANGUAGES.map((lang) => (
                <MenuItemRadio key={lang} name="language" value={lang}>
                  {t(`layout.languageSelector.${lang}`)}
                </MenuItemRadio>
              ))}
            </>
          )}

          <MenuDivider />
          <div className="app-header__menu-section">
            {t("layout.userMenu.support")}
          </div>
          <MenuItem onClick={handleReplayTour}>
            {t("layout.userMenu.replayTour")}
          </MenuItem>
          <MenuItem onClick={handleClearCache}>
            {t("layout.userMenu.clearCache")}
          </MenuItem>
          <MenuItem onClick={handleClearCacheAndCookies}>
            {t("layout.userMenu.clearCookies")}
          </MenuItem>
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}
